import React from "react";
import { Link } from "react-router-dom";
import "./EmergencyMedical.css";

const TREATMENT = [
  "Emergency hospital treatment",
  "Doctor visits & surgery",
  "Prescription medication",
  "Ambulance services",
];

const EVACUATION = [
  "Transport to nearest suitable hospital",
  "Air ambulance if needed",
  "Return to home country (repatriation)",
];

const WHO_COVERED = ["Cardholder", "Spouse", "Dependent children traveling with you"];

const CONDITIONS = [
  "Pay for the trip with your Visa card",
  "Must be a medical emergency (not planned care)",
  "Only while traveling abroad or away from home",
  "Trips up to 90 days",
  "Keep all receipts and medical documents",
  "File claims within required timeframe",
];

const EXCLUSIONS = [
  "Pre-existing medical conditions",
  "Routine or elective treatment",
  "High-risk / extreme sports",
  "Alcohol or drug-related incidents",
];

export default function EmergencyMedical() {
  return (
    <div className="em-page">
      <Link to="/benefits" className="back-link">← Benefits</Link>

      <section className="em-hero">
        <h1 className="em-title">Emergency Medical</h1>
        <p className="em-subtitle">24/7 emergency medical coverage worldwide. Activated automatically when you travel.</p>
      </section>

      <div className="em-highlights">
        <div className="em-highlight">
          <span className="em-highlight-value">$500K</span>
          <span className="em-highlight-label">Coverage</span>
        </div>
        <div className="em-highlight">
          <span className="em-highlight-value">24/7</span>
          <span className="em-highlight-label">Assistance</span>
        </div>
        <div className="em-highlight">
          <span className="em-highlight-value">90</span>
          <span className="em-highlight-label">Days / trip</span>
        </div>
      </div>

      {/* Who's covered */}
      <section className="em-section">
        <h3 className="em-section-title">Who's Covered</h3>
        <div className="em-pills">
          {WHO_COVERED.map((person, i) => (
            <span key={i} className="em-pill">✓ {person}</span>
          ))}
        </div>
      </section>

      {/* Treatment */}
      <section className="em-section">
        <div className="em-group-header">
          <div className="em-group-icon treatment">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="4" />
              <path d="M12 8v8" />
              <path d="M8 12h8" />
            </svg>
          </div>
          <h3 className="em-group-title">Treatment</h3>
        </div>
        <div className="em-list">
          {TREATMENT.map((t, i) => (
            <div key={i} className="em-list-item">
              <span className="em-check">✓</span>
              <span className="em-text">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Evacuation */}
      <section className="em-section">
        <div className="em-group-header">
          <div className="em-group-icon evacuation">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L12 16" />
              <path d="M8 6l4-4 4 4" />
              <path d="M4 22h16" />
              <path d="M4 18h16" />
            </svg>
          </div>
          <h3 className="em-group-title">Evacuation & Repatriation</h3>
        </div>
        <div className="em-list">
          {EVACUATION.map((e, i) => (
            <div key={i} className="em-list-item">
              <span className="em-check">✓</span>
              <span className="em-text">{e}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Dental */}
      <section className="em-section">
        <div className="em-group-header">
          <div className="em-group-icon dental">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 3c-2 0-4 2-4 5s1 4 2 7c.5 1.5 1 4 2 4s1.5-2 2-3.5c.3-.8.6-1.5 1-1.5s.7.7 1 1.5c.5 1.5 1 3.5 2 3.5s1.5-2.5 2-4c1-3 2-4 2-7s-2-5-4-5c-1 0-2 1-3 1s-2-1-3-1z" />
            </svg>
          </div>
          <h3 className="em-group-title">Emergency Dental</h3>
        </div>
        <div className="em-list">
          <div className="em-list-item">
            <span className="em-check limited">~</span>
            <span className="em-text">Urgent dental treatment (pain / injury) — limited coverage</span>
          </div>
        </div>
      </section>

      {/* Emergency SOS */}
      <section className="em-section">
        <div className="em-sos-card">
          <div className="em-sos-left">
            <span className="em-sos-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            </span>
            <div>
              <span className="em-sos-title">24/7 Emergency Hotline</span>
              <span className="em-sos-sub">Medical referrals · Emergency coordination</span>
            </div>
          </div>
          <span className="em-sos-action">Call</span>
        </div>
      </section>

      {/* Conditions */}
      <section className="em-section">
        <h3 className="em-section-title">Key Conditions</h3>
        <div className="em-list">
          {CONDITIONS.map((c, i) => (
            <div key={i} className="em-list-item">
              <span className="em-check">✓</span>
              <span className="em-text">{c}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Exclusions */}
      <section className="em-section">
        <h3 className="em-section-title">Exclusions</h3>
        <div className="em-list">
          {EXCLUSIONS.map((e, i) => (
            <div key={i} className="em-list-item">
              <span className="em-x">✕</span>
              <span className="em-text">{e}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="em-bottom">
        <span className="em-bottom-check">✓ Included</span>
        <span className="em-bottom-dot">·</span>
        <span className="em-bottom-check">✓ Auto-activated</span>
        <span className="em-bottom-dot">·</span>
        <span className="em-bottom-check">✓ No registration</span>
      </div>

      <div className="em-close-wrap">
        <Link to="/benefits" className="em-close">Close</Link>
      </div>

      <footer className="powered-by">Powered by Visa</footer>
    </div>
  );
}
